import type { CategoryInfo, CategorySlug, ExampleDefinition } from './types'

import { ButtonHover } from './examples/micro-interactions/ButtonHover'
import { ToggleSwitch } from './examples/micro-interactions/ToggleSwitch'
import { SlideFade } from './examples/page-transitions/SlideFade'
import { CrossfadeScale } from './examples/page-transitions/CrossfadeScale'
import { PhotoFlip } from './examples/page-transitions/PhotoFlip'
import { RevealOnScroll } from './examples/scroll-effects/RevealOnScroll'
import { ParallaxLayers } from './examples/scroll-effects/ParallaxLayers'
import { SkeletonShimmer } from './examples/loading-feedback/SkeletonShimmer'
import { SuccessCheckmark } from './examples/loading-feedback/SuccessCheckmark'
import { ExpandableCard } from './examples/layout-animations/ExpandableCard'
import { TabTransition } from './examples/layout-animations/TabTransition'
import { CascadingGrid } from './examples/stagger-patterns/CascadingGrid'
import { TextLetterByLetter } from './examples/stagger-patterns/TextLetterByLetter'
import { LineDrawing } from './examples/svg-shape/LineDrawing'
import { ShapeMorph } from './examples/svg-shape/ShapeMorph'
import { DraggableSpringBack } from './examples/drag-physics/DraggableSpringBack'
import { SwipeToDismiss } from './examples/drag-physics/SwipeToDismiss'

export const CATEGORIES: CategoryInfo[] = [
  { slug: 'micro-interactions', label: 'Micro Interactions', description: 'Hover, tap, and focus feedback', color: 'bg-rose-500' },
  { slug: 'page-transitions', label: 'Page Transitions', description: 'Enter and exit between views', color: 'bg-violet-500' },
  { slug: 'scroll-effects', label: 'Scroll Effects', description: 'Scroll-triggered reveals and parallax', color: 'bg-sky-500' },
  { slug: 'loading-feedback', label: 'Loading & Feedback', description: 'Skeletons, spinners, and confirmations', color: 'bg-emerald-500' },
  { slug: 'layout-animations', label: 'Layout Animations', description: 'Expanding, collapsing, and reordering', color: 'bg-amber-500' },
  { slug: 'stagger-patterns', label: 'Stagger Patterns', description: 'Sequential cascading animations', color: 'bg-orange-500' },
  { slug: 'svg-shape', label: 'SVG & Shape', description: 'Path drawing and shape morphing', color: 'bg-indigo-500' },
  { slug: 'drag-physics', label: 'Drag & Physics', description: 'Draggable elements with spring physics', color: 'bg-teal-500' },
]

export const EXAMPLES: ExampleDefinition[] = [
  {
    id: 'button-hover',
    title: 'Button Hover',
    description: 'Scale up on hover, compress on tap with spring physics',
    category: 'micro-interactions',
    component: ButtonHover,
    editorConfig: {
      transform: { scale: 1.05 },
      transition: { type: 'spring', spring: { stiffness: 400, damping: 17, mass: 1, bounce: 0.25 } },
    },
    trigger: 'hover',
  },
  {
    id: 'toggle-switch',
    title: 'Toggle Switch',
    description: 'iOS-style toggle with layout animation on the knob',
    category: 'micro-interactions',
    component: ToggleSwitch,
    trigger: 'click',
  },
  {
    id: 'slide-fade',
    title: 'Slide & Fade',
    description: 'Content slides in from the left with a fade, loops automatically',
    category: 'page-transitions',
    component: SlideFade,
    editorConfig: {
      transform: { x: -60, opacity: 0 },
      transition: { type: 'spring', spring: { stiffness: 260, damping: 20, mass: 1, bounce: 0.25 } },
    },
    trigger: 'auto',
  },
  {
    id: 'photo-flip',
    title: 'Photo Flip',
    description: '3D card flip reveals an Unsplash photo with perspective',
    category: 'page-transitions',
    component: PhotoFlip,
    trigger: 'auto',
  },
  {
    id: 'crossfade-scale',
    title: 'Crossfade Scale',
    description: 'Pages cross-fade with a subtle scale shift',
    category: 'page-transitions',
    component: CrossfadeScale,
    editorConfig: {
      transform: { scale: 0.9, opacity: 0 },
      transition: { type: 'tween', tween: { duration: 0.4, ease: 'easeInOut' } },
    },
    trigger: 'auto',
  },
  {
    id: 'reveal-on-scroll',
    title: 'Reveal on Scroll',
    description: 'Cards fade and slide up as they enter the viewport',
    category: 'scroll-effects',
    component: RevealOnScroll,
    trigger: 'auto',
  },
  {
    id: 'parallax-layers',
    title: 'Parallax Layers',
    description: 'Three layers shift at different depths following your cursor',
    category: 'scroll-effects',
    component: ParallaxLayers,
    trigger: 'hover',
  },
  {
    id: 'skeleton-shimmer',
    title: 'Skeleton Shimmer',
    description: 'Placeholder card with a sweeping shimmer gradient',
    category: 'loading-feedback',
    component: SkeletonShimmer,
    trigger: 'auto',
  },
  {
    id: 'success-checkmark',
    title: 'Success Checkmark',
    description: 'SVG circle draws in, then a checkmark springs to life',
    category: 'loading-feedback',
    component: SuccessCheckmark,
    trigger: 'auto',
  },
  {
    id: 'expandable-card',
    title: 'Expandable Card',
    description: 'Card smoothly expands to reveal hidden content',
    category: 'layout-animations',
    component: ExpandableCard,
    trigger: 'click',
  },
  {
    id: 'tab-transition',
    title: 'Tab Transition',
    description: 'Shared layoutId underline glides between tabs',
    category: 'layout-animations',
    component: TabTransition,
    trigger: 'click',
  },
  {
    id: 'cascading-grid',
    title: 'Cascading Grid',
    description: '3x4 grid of tiles stagger-animate in with variants',
    category: 'stagger-patterns',
    component: CascadingGrid,
    trigger: 'auto',
  },
  {
    id: 'text-letter-by-letter',
    title: 'Text Letter by Letter',
    description: '"Nimble Labs" letters pop in one by one with stagger',
    category: 'stagger-patterns',
    component: TextLetterByLetter,
    trigger: 'auto',
  },
  {
    id: 'line-drawing',
    title: 'Line Drawing',
    description: 'SVG shapes draw themselves in via pathLength',
    category: 'svg-shape',
    component: LineDrawing,
    trigger: 'auto',
  },
  {
    id: 'shape-morph',
    title: 'Shape Morph',
    description: 'Square morphs to circle with rotation and scale',
    category: 'svg-shape',
    component: ShapeMorph,
    editorConfig: {
      transform: { rotate: 180, scale: 1.1 },
      transition: { type: 'spring', spring: { stiffness: 200, damping: 20, mass: 1, bounce: 0.25 } },
    },
    trigger: 'auto',
  },
  {
    id: 'draggable-spring-back',
    title: 'Draggable Spring Back',
    description: 'Drag a card anywhere — it springs back to center',
    category: 'drag-physics',
    component: DraggableSpringBack,
    trigger: 'hover',
  },
  {
    id: 'swipe-to-dismiss',
    title: 'Swipe to Dismiss',
    description: 'Cards swipe away horizontally, then respawn',
    category: 'drag-physics',
    component: SwipeToDismiss,
    trigger: 'hover',
  },
]

export function getExamplesByCategory(slug?: CategorySlug): ExampleDefinition[] {
  if (!slug) return EXAMPLES
  return EXAMPLES.filter((e) => e.category === slug)
}

export function getCategoryBySlug(slug: string): CategoryInfo | undefined {
  return CATEGORIES.find((c) => c.slug === slug)
}

export function getExampleById(id: string): ExampleDefinition | undefined {
  return EXAMPLES.find((e) => e.id === id)
}
